import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtMoney } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Building2, Plus, Pencil, Trash2, ChevronRight } from "lucide-react";

const empty = { name: "", phone: "", payment_terms: "", notes: "" };

export default function ProClients() {
  const nav = useNavigate();
  const { isAdmin, settings } = useAuth();
  const cur = settings?.currency || "KMF";
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = useCallback(() => {
    api.get("/pro/clients").then((r) => setClients(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/pro/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ name: c.name, phone: c.phone || "", payment_terms: c.payment_terms || "", notes: c.notes || "" }); setEditId(c.id); setOpen(true); };
  const save = async () => {
    if (!form.name.trim()) return toast.error("Nom requis");
    try {
      if (editId) await api.put(`/pro/clients/${editId}`, form);
      else await api.post("/pro/clients", form);
      toast.success("Client enregistré"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer ce client professionnel ?")) return;
    try { await api.delete(`/pro/clients/${id}`); toast.success("Supprimé"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const Stat = ({ label, value }) => (
    <Card className="p-4"><p className="text-xs text-slate-500">{label}</p><p className="text-lg font-bold text-slate-800 mt-1">{value}</p></Card>
  );

  return (
    <div className="space-y-6" data-testid="pro-clients-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-display font-extrabold flex items-center gap-2"><Building2 className="text-sky-600" /> Professionnels</h1>
          <p className="text-slate-500 text-sm">Établissements, commandes cumulées et factures périodiques</p>
        </div>
        {isAdmin && <Button data-testid="new-pro-client-btn" onClick={openNew} className="bg-sky-600 hover:bg-sky-700"><Plus size={16} className="mr-1.5" /> Nouveau client</Button>}
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="pro-global-stats">
          <Stat label="Total kg" value={`${stats.total_kg} kg`} />
          <Stat label="Montant total" value={fmtMoney(stats.total_amount, cur)} />
          <Stat label="Facturé" value={fmtMoney(stats.billed_amount, cur)} />
          <Stat label="Encaissé" value={fmtMoney(stats.collected, cur)} />
          <Stat label="Reste à encaisser" value={fmtMoney(stats.remaining, cur)} />
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {clients.map((c) => (
          <Card key={c.id} data-testid={`pro-client-${c.id}`} className="p-4 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between gap-2">
              <button onClick={() => nav(`/pro/clients/${c.id}`)} className="text-left min-w-0">
                <p className="font-semibold text-slate-800 truncate">{c.name}</p>
                <p className="text-sm text-slate-500">{c.phone || "—"}</p>
                {c.payment_terms && <p className="text-xs text-slate-400 mt-0.5">Règlement : {c.payment_terms}</p>}
              </button>
              {isAdmin && (
                <div className="flex gap-1 shrink-0">
                  <Button size="icon" variant="ghost" data-testid={`edit-pro-client-${c.id}`} onClick={() => openEdit(c)}><Pencil size={15} /></Button>
                  <Button size="icon" variant="ghost" className="text-red-600" data-testid={`delete-pro-client-${c.id}`} onClick={() => remove(c.id)}><Trash2 size={15} /></Button>
                </div>
              )}
            </div>
            <Button variant="outline" size="sm" className="mt-3 w-full" data-testid={`open-pro-client-${c.id}`} onClick={() => nav(`/pro/clients/${c.id}`)}>Ouvrir la fiche <ChevronRight size={15} className="ml-1" /></Button>
          </Card>
        ))}
        {clients.length === 0 && <p className="text-slate-400 col-span-full text-center py-8">Aucun client professionnel. {isAdmin ? "Ajoutez-en un." : ""}</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="pro-client-dialog">
          <DialogHeader><DialogTitle>{editId ? "Modifier" : "Nouveau"} client professionnel</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-sm text-slate-600">Nom de l'établissement *</label><Input data-testid="pro-client-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><label className="text-sm text-slate-600">Téléphone</label><Input data-testid="pro-client-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><label className="text-sm text-slate-600">Conditions de règlement</label><Input data-testid="pro-client-terms" value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} placeholder="Ex: 10 jours dès réception" /></div>
            <div><label className="text-sm text-slate-600">Observations (facultatif)</label><Input data-testid="pro-client-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button><Button data-testid="save-pro-client" onClick={save} className="bg-sky-600 hover:bg-sky-700">Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
